import { Glyph, GlyphChange, GlyphLayer, GlyphState, IdeaBox, IdeaBoxEntry, Project, Violation, ViolationType, ViolationVisitor, Violations } from "konect-model";
import { getContrast } from "./util";
import { MXGraphService } from "./mxgraph-service";

export class ViolationService {

    public static init() {
        Violations.registerViolationVisitor(new ColorVisitor());
        Violations.registerViolationVisitor(new UnusedVisitor());
        Violations.registerViolationVisitor(new ChoosenAttributeVisitor());
        Violations.registerViolationVisitor(new ShapeVisitor());
        //Violations.registerViolationVisitor(new TemplateVisitor());
    }

    public static calculateViolations(project: Project) {
        const violations = Violations.calculate(project);
        return violations
    }
}

// Use this is as a template to access all available visiting functions a violation visitor can hold
export class TemplateVisitor extends ViolationVisitor {
    templateHelperVariable: string[] = [] //user helper variables to store any information gathered in one visitor function that is needed in another one

    protected startVisit(project: Project): void {
        // initialize all helper variables, otherwise they will start with their previous values once the visitor is used again
        this.templateHelperVariable = [];
    }

    protected visitIdeaBox(box: IdeaBox, project: Project): void { }

    protected visitIdeaBoxEntry(entry: IdeaBoxEntry, box: IdeaBox, project: Project): void { }

    protected visitGlyph(glyph: Glyph): void { }

    protected visitGlyphLayer(layer: GlyphLayer, glyph: Glyph, project: Project): void { }

    protected visitGlyphIdeaBoxEntry(entry: IdeaBoxEntry, layer: GlyphLayer, glyph: Glyph, project: Project): void { }

    protected visitGlyphState(state: GlyphState, glyph?: Glyph, project?: Project): void { }

    protected visitStateChange(change: GlyphChange, state: GlyphState, glyph?: Glyph, project?: Project): void { }

    protected endVisit(project: Project): void {
    }
}

// this is an examplary violation visitor for evaluating the complexity of used shapes
export class ShapeVisitor extends ViolationVisitor {
    shapes: { shape: string, complexity: number }[] = [];

    protected startVisit(project: Project): void {
        this.shapes = [];
    }

    protected visitStateChange(change: GlyphChange): void {
        if (!change.style) return;

        // TODO add all shapes. 
        // Complexity is determined by different visual appearances when rotating a shape by 90deg and mirroring it 
        // (see "Supporting Supervisory Control of Saftey-Critical Systems with Rationally Justified information Visualizations (Harre 2019)" (Harre 2019))
        let shape = "rectangle";
        let complexity = 1;
        if (change.style.triangle) {
            shape = "triangle";
            complexity = 4;
        } else if (change.style.ellipse) {
            shape = "ellipse";
            complexity = 2;
        } else if (change.style.rhombus) {
            shape = "rhombus";
            complexity = 1;
        } else if (change.style.shape) {
            shape = change.style.shape
            complexity = 8 // 8 is the highest shape complexity. TODO check shapes that use style.shape but are of low complexity (e.g. like the parallelogram down below)
            switch (change.style.shape) {
                case "parallelogram":
                    complexity = 4;
                    break;
                case "partialRectangle":
                    complexity = 2;
                    break;
            }
        }
        this.shapes.push({ shape: shape, complexity: complexity })
    }

    protected endVisit(): void {
        if (this.shapes.length <= 0) return;
        let overallSum = 0;
        let complexShapeSum = 0;
        for (const s of this.shapes) {
            overallSum += s.complexity
            s.complexity > 4 && (complexShapeSum += s.complexity)
        }
        if (overallSum < this.shapes.length * 4) {
            return;
        }
        const violation = new Violation("shape-violation", "Many Complex Shapes", ViolationType.SIMPLICITY_SHAPE);
        violation.description = `A lot of used shapes might be of higher complexity (complexity increases with an increased asymmetry of a shape.) and thus might be harder to comprehend.`;
        const amountComplexShapes = this.shapes.filter(s => s.complexity > 4).length;
        const complexShapeRatio = amountComplexShapes / this.shapes.length;
        violation.weight = complexShapeSum * complexShapeRatio;
        this.violations.push(violation);
    }
}

export class ColorVisitor extends ViolationVisitor {
    uniqueColors: string[] = [];
    defaultStateColors: string[] = [];
    uniqueCells: string[] = [];
    unreadableTextAmount = 0;

    protected visitStateChange(change: GlyphChange, state: GlyphState, glyph: Glyph) {
        !this.uniqueCells.find(id => id === change.cellId) && this.uniqueCells.push(change.cellId);
        let style = change.style || {};
        for (const value of Object.values(style)) {
            if (typeof value !== 'string' || value.charAt(0) !== "#" || value.length !== 7) continue
            if (!this.uniqueColors.find(c => c === value)) {
                this.uniqueColors.push(value);
            }
            if (state.id === "default") {
                this.defaultStateColors.push(value);
            }
        }

        const textColorPairs = this.getFillAndLabelColorOfTextChange(change, state, glyph);

        for (const [i, valueColorPair] of textColorPairs.valueColorPairs.entries()) {
            const contrast = getContrast(valueColorPair[1], textColorPairs.fillColor);
            if (contrast < 3) { // < 3 indicates hard to read text
                const violation = new Violation("color-violation" + change.cellId + state.id + i, "Low Text Contrast", ViolationType.SIMPLICITY_COLOR);
                violation.description = `The <${valueColorPair[0]}> text element in <${glyph.name} - ${state.name}>  might be hard to read due to low color contrast (contrast ratio ${(contrast).toFixed(2)} ≤ 3).`;
                this.violations.push(violation);
            }
        }
    }

    private getFillAndLabelColorOfTextChange(change: GlyphChange, state: GlyphState, glyph: Glyph): { valueColorPairs: string[][], fillColor: string } {
        let style = change.style || {};
        let value = change.value;
        if (state.id !== "default") {
            const defaultState = this.project.getDefaultGlyphState();
            const defaultChange = defaultState.changes.find(c => c.cellId === change.cellId);
            const defaultValue = defaultChange?.value
            const defaultStyle = defaultChange?.style;
            defaultStyle && (style = Object.assign({ ...defaultStyle }, style))
            !value && defaultValue && (value = defaultValue)
        }

        // return if there is no value to read
        if (!value) return {
            valueColorPairs: [],
            fillColor: ""
        };

        let fillColor = style.fillColor;

        //TODO handle transparent text backgrounds by checking elements beneath properly (currently it is assumed all shapes are rectangular).
        if (fillColor === "none") {
            const defaultState = glyph.getDefaultState();
            const cgeom = change.geometry;
            let geom = defaultState.changes.find(c => c.cellId === change.cellId)?.geometry;
            if (!geom) return {
                valueColorPairs: [],
                fillColor: ""
            };
            geom = {...geom}
            if (cgeom && state.id !== "default") {
                geom.x += cgeom.x || 0;
                geom.y += cgeom.y || 0;
                geom.width += cgeom.width || 0;
                geom.height += cgeom.height || 0;
            }

            let changes = state.id === "default" ?  state.changes :  [...state.changes, ...defaultState.changes];

            //order changes by z index
            if (glyph.model) {
                const children = MXGraphService.getAllChildrenOfCell(glyph.model).reverse();
                let zIndexOrderedChanges : GlyphChange[] = [];
                for (const child of children) {
                    //@ts-ignore FIX child.Id or zIndexing wont work properly
                    zIndexOrderedChanges = [...zIndexOrderedChanges, ...changes.filter(c => c.cellId === child.Id)];   
                }
                changes = zIndexOrderedChanges;
            }
            
            for (const c of changes) { // todo calculate area in states.
                if (!c || c.cellId === change.cellId || !c.style || !c.geometry) continue

                if ((c.style.hide && c.style.hide === "1") || (c.style.fillColor && c.style.fillColor === "none")) {
                    continue
                }
                const [x1, y1, w1, h1] = [c.geometry.x, c.geometry.y, c.geometry.x + c.geometry.width, c.geometry.y + c.geometry.height]; 
                const [x2, y2, w2, h2] = [geom.x, geom.y, geom.x + geom.width, geom.y + geom.height]; 
                const ow = Math.min(w1, w2) - Math.max(x1, x2);
                const oh = Math.min(h1, h2) - Math.max (y1, y2);

                const overlapArea = ow <= 0 || oh <= 0 ? 0 : ow * oh;

                // if the two areas overlap more than 25%, use the fill color
                if (overlapArea / (w1 - x1) * (h1 - y1) > 0.25 || overlapArea / (w2 - x2) * (h2 - y2) > 0.25) {
                    fillColor = c.style.fillColor || "#ffffff";
                    break;
                }
            }
            if (fillColor === "none") {
                return {
                    valueColorPairs: [],
                    fillColor: ""
                };
            }
        }

        const textAndColors = value.replace("<span>", "").replace("</span>", "").replace("<br>", "").replace("<b>", "").replace("</b>", "").split("<")
        const pairs = [];
        for (const entry of textAndColors) {
            if (!entry.includes("color=")) {
                const update = entry.replace("/font>", "")
                if (update === "") {
                    continue
                }
                pairs.push([update, style.fontColor ? style.fontColor : "#000000"]);
            } else {
                const labelColor = entry.match(/color="#\w{6}"/g)
                let color = labelColor ? labelColor[0] : "#000000"
                const text = entry.split(">")[1];
                text && pairs.push([text, color.replace('color="', "").replace('"', "")]);
            }
        }

        if (!fillColor) {
            fillColor = "#ffffff"
        }

        if (pairs.length <= 0) {
            return {
                valueColorPairs: [],
                fillColor: ""
            };
        }
        return {
            valueColorPairs: pairs,
            fillColor: fillColor
        }
    }

    protected endVisit() {
        /**
        const defaultContrastAverage = getAverageContrast(this.defaultStateColors);
        if (defaultContrastAverage > 2.5) { // possibly many color changes
            const violation = new Violation("color-violation" + defaultContrastAverage, "High Average Contrast", ViolationType.SIMPLICITY_COLOR);
            violation.description = `The default state of your design might use too many high contrasting colors. Try to reduce the color contrast of non-important information elements (or the contrast between similar information elements) as much as possible.`;
            violation.weight = 2; //[...new Set(this.defaultStateColors)].length;
            this.violations.push(violation);
        }
        if (this.uniqueColors.length >= 12 && getAverageContrast(this.uniqueColors) > 2.5) {
            const violation = new Violation("color-violation" + this.uniqueColors.length, "Use of Colors", ViolationType.SIMPLICITY_COLOR);
            violation.description = `Overall there are many high contrasting different colors in use. Your design might be too colorful, reducing visual clarity. Always try to reduce the amount of colors as much as possible.`;
            violation.weight = this.uniqueColors.length / this.uniqueCells.length;
            this.violations.push(violation);
        }
        */
        // reset all helper variables, otherwise they will start with their previous values once the visitor is used again
        this.uniqueColors = [];
        this.defaultStateColors = [];
        this.uniqueCells = [];
        this.unreadableTextAmount = 0;
    }
}

export class UnusedVisitor extends ViolationVisitor {
    allEntries: IdeaBoxEntry[] = [];

    protected visitIdeaBoxEntry(entry: IdeaBoxEntry): void {
        this.allEntries.push(entry);
    }

    protected visitGlyphIdeaBoxEntry(entry: IdeaBoxEntry): void {
        if (!entry.selectedAttribute) {
            return
        }
        let index = this.allEntries.findIndex(el => el.id === entry.id);
        if (index < 0) return
        this.allEntries.splice(index, 1);
    }

    protected endVisit() {
        if (this.allEntries.length <= 0) return []
        const violation = new Violation("unused-entry-violation" + this.allEntries.length, "Unused Tasks", ViolationType.ADDITIONAL_INFORMATION);
        violation.description = `There are currently ${this.allEntries.length} tasks that are not being used in Glyphs.`;
        violation.weight = 0.25
        violation.importance = 0.5
        this.violations.push(violation);

        // reset all helper variables, otherwise they will start with their previous values once the visitor is used again
        this.allEntries = [];
    }
}

export class ChoosenAttributeVisitor extends ViolationVisitor {

    protected visitGlyphIdeaBoxEntry(entry: IdeaBoxEntry): void {
        if (!entry.selectedAttribute) return;
        const index = entry.availableAttributes.findIndex(el => entry.selectedAttribute.id === el.id);
        const weight = index / (entry.availableAttributes.length - 1);
        if (weight <= 0) return
        const violation = new Violation(entry.id + entry.selectedAttribute.id, "Unoptimized Attribute (" + index + ")", ViolationType.CHOSEN_ATTRIBUTE);
        violation.description = `<${entry.task.name}> uses <${entry.selectedAttribute.name}> as attribute. Try to use <${entry.availableAttributes[0].name}> instead.`;
        violation.weight = weight
        this.violations.push(violation);
    }

}